import React, { useRef, useState } from 'react';
import { FaEye, FaTimes, FaPaperclip } from 'react-icons/fa';
import { FormModal } from './Modals';
import { analyticalMethodsAPI } from '../services/api';
import { toast } from 'react-toastify';

const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');

// Gate shown before an already-Approved record is edited: a Change Control /
// Deviation / CAPA reference is always required; when no QMS integration is
// linked for this client (qmsLinked=false) a scanned copy of the paper record
// is required too, mirroring AcceptanceCriteria.js's AmvCell pattern.
const ChangeReferenceModal = ({ open, qmsLinked, busy, onCancel, onConfirm, recordLabel }) => {
  const [referenceType, setReferenceType] = useState('Change Control');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  if (!open) return null;

  const handleUpload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await analyticalMethodsAPI.uploadFile(fd);
      const uploaded = res?.data?.data;
      if (!uploaded) throw new Error('Upload failed');
      setFiles((f) => [...f, { filename: uploaded.filename, originalname: uploaded.originalname }]);
    } catch {
      toast.error('Attachment upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = (filename) => {
    setFiles((f) => f.filter((x) => x.filename !== filename));
    analyticalMethodsAPI.deleteFile(filename).catch(() => {});
  };

  const canSubmit = referenceType && referenceNumber.trim() && (qmsLinked || files.length > 0);

  const handleSubmit = () => {
    if (!canSubmit) return;
    onConfirm({ reference_type: referenceType, reference_number: referenceNumber.trim(), reference_files: files });
  };

  return (
    <FormModal isOpen={open} onClose={onCancel} onSubmit={handleSubmit} isLoading={busy}
      title="Change Control / Deviation / CAPA Reference" size="sm">
      <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', marginTop: 0 }}>
        {recordLabel ? `"${recordLabel}" is` : 'This record is'} Approved. Editing it must cite a Change Control, Deviation
        or CAPA reference{!qmsLinked ? ', with a scanned copy of that record since no QMS integration is linked for this client' : ' — AiventraQMS is linked, so the reference number alone is accepted'}.
      </p>

      <div style={{ marginBottom: '10px' }}>
        <label style={{ display: 'block', fontSize: 'var(--fs-xs)', marginBottom: '3px' }}>Reference Type</label>
        <select className="form-control form-control-sm" value={referenceType} onChange={(e) => setReferenceType(e.target.value)}>
          <option value="Change Control">Change Control</option>
          <option value="Deviation">Deviation</option>
          <option value="CAPA">CAPA</option>
        </select>
      </div>

      <div style={{ marginBottom: '10px' }}>
        <label style={{ display: 'block', fontSize: 'var(--fs-xs)', marginBottom: '3px' }}>Reference Number</label>
        <input className="form-control form-control-sm" value={referenceNumber}
          onChange={(e) => setReferenceNumber(e.target.value)} placeholder="e.g. CC-2026-0145" />
      </div>

      {!qmsLinked && (
        <div>
          <label style={{ display: 'block', fontSize: 'var(--fs-xs)', marginBottom: '3px' }}>Scanned Copy (required)</label>
          {files.map((f) => (
            <div key={f.filename} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px', fontSize: 'var(--fs-xs)' }}>
              <a href={`${API_ORIGIN}/uploads/${f.filename}`} target="_blank" rel="noopener noreferrer" title={f.originalname}
                style={{ color: 'var(--primary-color)', textDecoration: 'none', maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <FaEye style={{ marginRight: '4px' }} />{f.originalname || f.filename}
              </a>
              <button type="button" onClick={() => handleRemove(f.filename)} title="Remove"
                style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--danger-color)', padding: 0, lineHeight: 1 }}>
                <FaTimes />
              </button>
            </div>
          ))}
          <input ref={inputRef} type="file" accept="application/pdf,image/*" style={{ display: 'none' }}
            onChange={(e) => { if (e.target.files[0]) handleUpload(e.target.files[0]); e.target.value = ''; }} />
          <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
            style={{ fontSize: 'var(--fs-xs)', padding: '4px 9px', borderRadius: '4px', border: '1px dashed var(--border-color)', background: 'transparent', cursor: 'pointer', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <FaPaperclip />{uploading ? 'Uploading…' : 'Attach scanned copy (PDF/image)'}
          </button>
        </div>
      )}
    </FormModal>
  );
};

export default ChangeReferenceModal;
